import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useEffect, useState } from "react";

import { formatAmount } from "@/lib/format";
import { showError, showSuccess } from "@/lib/toast";
import {
  copyQuotationProcesses,
  listItemPastRecords,
  listQuotationProcesses,
  type PastRecord,
} from "./api";

/** 金額の欄。入っていない値は「—」にする（0 円と区別する）。 */
function Money({ value }: { value: string | null }) {
  if (value == null) return <span className="text-default-300">—</span>;
  return <>{formatAmount(value)}</>;
}

/**
 * 同じ図番を過去にいくらで出し、いくらで受注したかを並べるダイアログ（実績参照）。
 * **見積入力画面の「実績参照」から開く**。
 *
 * 金額を入れるときに最初に知りたいのは「前はいくらだったか」。同じ部品が何度も
 * 来る商売なので、過去と大きく違う単価を出すと客先に理由を訊かれる。これまでは
 * 案件管理と見積管理を行き来して探していた。
 *
 * 1 行は**過去の案件明細**で、案件側（受注日・受注単価）と見積側（希望単価・
 * 回答単価）を並べる。見積が紐付いていない行（特急フローで起票しただけの案件）も
 * 落とさずに出す。「受注はあったが見積が残っていない」ことが分かるのも情報で、
 * 黙って消すと初めて見る図番に見えてしまう。
 *
 * **工程の内訳を写せるのは見積が紐付いている行だけ**（写す中身がそこにしか
 * 無い）。写すと**いまの入力は捨てられる**ので、既に入力がある明細では確認を
 * 挟む。足す形にすると同じ工程が 2 行並び、単価が写した見積の倍になるため、
 * 置き換え以外の選択肢は持たせていない。
 */
export default function ItemPastRecordsDialog({
  quotationId,
  itemId,
  drawingNumber,
  isOpen,
  canWrite,
  onClose,
}: {
  quotationId: number;
  itemId: number;
  drawingNumber: string;
  isOpen: boolean;
  /** 複製できるか。受注済みの見積や除外した行では読むだけにする。 */
  canWrite: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  /** 置き換えの確認を待っている行（null なら確認中ではない）。 */
  const [pending, setPending] = useState<PastRecord | null>(null);

  // 開き直したら確認は白紙に戻す（前に選んだ行が残っていると、別の行を写した
  // つもりで前の行が入る）。
  useEffect(() => {
    if (isOpen) setPending(null);
  }, [isOpen]);

  const records = useQuery({
    queryKey: ["quotation-item-past-records", quotationId, itemId],
    queryFn: () => listItemPastRecords(quotationId, itemId),
    enabled: isOpen,
  });

  // いまの明細の内訳。**見積入力の右半分と同じキー**なので、開いた時点で
  // 取り置きから返る（確認を出すかどうかと、捨てる金額の表示に使う）。
  const current = useQuery({
    queryKey: ["quotation-processes", quotationId, itemId],
    queryFn: () => listQuotationProcesses(quotationId, itemId),
    enabled: isOpen,
  });
  const currentRowCount = current.data?.items.length ?? 0;
  const currentUnitPrice = current.data?.total_price ?? "0";

  const copy = useMutation({
    mutationFn: (record: PastRecord) =>
      copyQuotationProcesses(quotationId, itemId, record.quotation_item_id!),
    onSuccess: async (_data, record) => {
      // 単価が変わるので、明細一覧と見積ヘッダ（合計金額）も引き直す。
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["quotation-processes", quotationId, itemId],
        }),
        queryClient.invalidateQueries({ queryKey: ["quotation-items", quotationId] }),
        queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] }),
        queryClient.invalidateQueries({ queryKey: ["quotations"] }),
      ]);
      showSuccess(
        `${record.quotation_number} の内訳を複製しました（${record.process_count} 工程）。`,
      );
      onClose();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setPending(null),
  });

  /** 押されたら、いまの入力があるときだけ確認を挟む。 */
  const requestCopy = (record: PastRecord) => {
    if (currentRowCount > 0) {
      setPending(record);
      return;
    }
    copy.mutate(record);
  };

  const rows = records.data?.items ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="5xl"
      scrollBehavior="inside"
      isDismissable={!copy.isPending}
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>実績参照</span>
          <span className="text-small font-normal text-default-500">
            図番 {drawingNumber} の過去の受注実績
            {currentRowCount > 0 && (
              <>
                {" / "}いまの単価 {formatAmount(currentUnitPrice)} 円
              </>
            )}
          </span>
        </ModalHeader>

        <ModalBody>
          {records.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner label="読み込み中..." />
            </div>
          ) : records.error ? (
            <p className="py-2 text-sm text-danger">
              実績を取得できませんでした。
              <br />
              {(records.error as Error).message}
            </p>
          ) : rows.length === 0 ? (
            <p className="py-2 text-sm text-default-500">
              この図番の過去の受注はありません。初めて見積る図番です。
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-default-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-default-50 text-left text-tiny text-default-600">
                  <tr className="h-8">
                    <th className="px-3 font-bold">工番</th>
                    <th className="px-3 font-bold">得意先</th>
                    <th className="px-3 font-bold">受注日</th>
                    <th className="px-3 text-right font-bold">数量</th>
                    <th className="px-3 text-right font-bold">希望単価</th>
                    <th className="px-3 text-right font-bold">回答単価</th>
                    <th className="px-3 text-right font-bold">受注単価</th>
                    <th className="px-3 font-bold">見積</th>
                    <th className="px-3 font-bold" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((record) => (
                    <tr
                      key={record.project_item_id}
                      className="h-10 border-t border-default-100"
                    >
                      <td className="px-3 font-medium whitespace-nowrap">
                        {record.project_number}
                      </td>
                      <td className="max-w-40 truncate px-3">
                        {record.client_name ?? "—"}
                      </td>
                      <td className="px-3 whitespace-nowrap">
                        {record.order_date ?? (
                          <span className="text-default-300">—</span>
                        )}
                      </td>
                      <td className="px-3 text-right">{record.quantity}</td>
                      <td className="px-3 text-right">
                        <Money value={record.target_unit_price} />
                      </td>
                      {/* 自社が出した単価。ここが今回の値決めの比較対象。 */}
                      <td className="px-3 text-right font-bold">
                        <Money value={record.quoted_unit_price} />
                      </td>
                      <td className="px-3 text-right">
                        <Money value={record.order_unit_price} />
                      </td>
                      <td className="px-3 whitespace-nowrap">
                        {record.quotation_number ? (
                          <span className="text-default-600">
                            {record.quotation_number}
                            <span className="text-default-400">
                              {" "}
                              {record.quotation_sequence} 行目
                            </span>
                          </span>
                        ) : (
                          // 特急で起票しただけの案件。値も内訳も残っていない。
                          <Chip size="sm" variant="flat" color="default">
                            見積なし
                          </Chip>
                        )}
                      </td>
                      <td className="px-3 whitespace-nowrap">
                        {record.copyable ? (
                          <Button
                            size="sm"
                            radius="full"
                            variant="bordered"
                            color="primary"
                            startContent={<Copy size={14} />}
                            // 同じ文言のボタンが並ぶので、読み上げにはどの実績かを乗せる。
                            aria-label={`${record.project_number} の内訳を複製する`}
                            isDisabled={!canWrite || copy.isPending}
                            onPress={() => requestCopy(record)}
                          >
                            複製
                          </Button>
                        ) : (
                          <span className="text-tiny text-default-400">
                            {record.quotation_item_id == null
                              ? "内訳なし"
                              : "工程未入力"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 置き換えの確認。別のダイアログを重ねず、選んだ行の下に出す
              （重ねると、どの行を選んだのかが隠れて見えなくなる）。 */}
          {pending && (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-warning-200 bg-warning-50 p-3 text-sm">
              <p className="min-w-0 flex-1">
                いまの明細に入っている内訳（{currentRowCount} 工程・
                {formatAmount(currentUnitPrice)} 円）を捨てて、
                <span className="font-bold">
                  {pending.quotation_number} {pending.quotation_sequence} 行目
                </span>
                の内訳（{pending.process_count} 工程・
                {formatAmount(pending.quoted_unit_price ?? "0")} 円）に置き換えます。
              </p>
              <Button
                size="sm"
                radius="full"
                variant="light"
                isDisabled={copy.isPending}
                onPress={() => setPending(null)}
              >
                キャンセル
              </Button>
              <Button
                size="sm"
                radius="full"
                color="warning"
                data-testid="past-record-copy-confirm"
                isLoading={copy.isPending}
                onPress={() => copy.mutate(pending)}
              >
                置き換える
              </Button>
            </div>
          )}

          {rows.length > 0 && (
            <p className="text-tiny text-default-500">
              複製すると、その見積の工程別内訳（時間・時間単価・金額・摘要）がそのまま入り、単価も複製元と同じになります。図番・品名・数量は今回の明細のままです。
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            radius="full"
            variant="light"
            data-testid="close"
            isDisabled={copy.isPending}
            onPress={onClose}
          >
            閉じる
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
